import React, { FormEvent, useCallback, useMemo, useState } from "react";
import * as styles from "./settings-team.module.scss";
import {
	VelavuAPI,
	VelavuRole,
	VelavuUser,
	VelavuUserStatus,
} from "velavu-js-api";
import { useTranslation } from "react-i18next";
import HoverTooltip from "../../elements/hover-tooltip";
import VelavuButton from "../../elements/velavu-button";
import VelavuInput from "../../elements/velavu-input";
import { useVelavuModal } from "../../elements/velavu-modal";
import VelavuAlertModal from "../../elements/velavu-alert-modal/velavu-alert-modal";
import VelavuAlertModalTitle from "../../elements/velavu-alert-modal/velavu-alert-modal-title";
import VelavuAlertModalContent from "../../elements/velavu-alert-modal/velavu-alert-modal-content";
import VelavuAlertModalActions from "../../elements/velavu-alert-modal/velavu-alert-modal-actions";
import VelavuNotice from "../../elements/velavu-notice/velavu-notice";
import { useCancellableEffect } from "../../helper/hook-helper";
import {
	mapUserRegistrationStatus,
	mapUserRole,
} from "../../helper/language-helper";
import { checkEmail } from "../../helper/validation-helper";
import {
	useSignInContext,
	useHasPermission,
} from "../sign-in-context/sign-in-context";
import { useVelavuToast } from "../../elements/velavu-toast";
import { ToastNotificationExternalError } from "../../elements/toast-notification-builtins";
import VelavuNoticeHeader from "../../elements/velavu-notice/velavu-notice-header";
import VelavuNoticeBody from "../../elements/velavu-notice/velavu-notice-body";
import VelavuAlertModalObject from "../../elements/velavu-alert-modal/velavu-alert-modal-object";
import { VelavuScrollableRoundTable } from "../../elements/velavu-scrollable-round-table";
import VelavuMenu, {
	VelavuMenuItem,
	VelavuOverflowButton,
} from "../../elements/velavu-menu";
import VelavuSelect from "../../elements/velavu-select";
import { classArr } from "../../helper/style-helper";
import {
	getAssignableRoles,
	getPermissionDeniedMessage,
	isRoleHigherThan,
	ROLE_HIERARCHY,
} from "../../helper/permission-helper";
import VelavuInlineSelect from "../../elements/velavu-inline-select";
import SettingsRolesTooltip from "./settings-roles-tooltip";

export default function SettingsTeam() {
	const { t } = useTranslation();
	const { pushToast } = useVelavuToast();

	const canWriteUsers = useHasPermission("users:write");
	const canDeleteUsers = useHasPermission("users:delete");

	const userContext = useSignInContext();
	const userOrganization = userContext.organization?.name;
	const userOrganizationReadable =
		userOrganization ?? t("common.organization");

	const velavuUser = userContext.velavuUser;
	const currentUserRole = velavuUser?.role;
	const userEmail = velavuUser?.email;
	const assignableRoles = useMemo(() => {
		if (currentUserRole === undefined) {
			return null;
		} else {
			return getAssignableRoles(currentUserRole);
		}
	}, [currentUserRole]);

	//Fetching users
	const [users, setUsers] = useState<VelavuUser[]>([]);

	useCancellableEffect((addPromise) => {
		addPromise(VelavuAPI.users.getAllUsers())
			.then(setUsers)
			.catch((error) => console.log(error.response));
	}, []);

	const addUser = useCallback(
		(user: VelavuUser) => {
			setUsers(users.concat(user));
		},
		[setUsers, users],
	);

	const updateUserRole = useCallback(
		(user: VelavuUser, role: VelavuRole) => {
			VelavuAPI.users
				.updateSpecificUserSettings(user.email, { role })
				.then((updatedUser) => {
					setUsers((users) => {
						const idx = users.findIndex(
							(user) => user.email === updatedUser.email,
						);
						if (idx === -1) {
							return users;
						}
						const updatedUsersArray = [...users];
						updatedUsersArray[idx] = updatedUser;
						return updatedUsersArray;
					});
				})
				.catch(() => {
					pushToast((close) => (
						<ToastNotificationExternalError onDismiss={close} />
					));
				});
		},
		[setUsers, users, pushToast],
	);

	const { pushModal } = useVelavuModal();

	const removeUser = useCallback(
		(user: VelavuUser) => {
			pushModal<boolean>((resolve) => (
				<VelavuAlertModal>
					<VelavuAlertModalTitle>
						{t("settings.organization.remove_user_title")}
					</VelavuAlertModalTitle>

					<VelavuAlertModalObject>{user.name}</VelavuAlertModalObject>

					<VelavuAlertModalContent>
						{t("settings.organization.remove_user_notice", {
							email: user.email,
							organization: userOrganizationReadable,
						})}
					</VelavuAlertModalContent>

					<VelavuAlertModalActions>
						<VelavuButton
							label={t("settings.organization.cancel")}
							variant="outlined"
							fullWidth
							onClick={() => resolve(false)}
						/>

						<VelavuButton
							label={t("settings.organization.remove")}
							variant="normal"
							fullWidth
							onClick={() => resolve(true)}
						/>
					</VelavuAlertModalActions>
				</VelavuAlertModal>
			)).then((result) => {
				if (!result) return;

				//Removing the user
				VelavuAPI.users
					.deleteUser(user.email)
					.then(() =>
						setUsers(
							users.filter(
								(allUsers) => allUsers.email !== user.email,
							),
						),
					)
					.catch((error) => console.log(error.response));
			});
		},
		[pushModal, setUsers, users, userOrganizationReadable, t],
	);

	return (
		<div className={styles.container}>
			<ListMembers
				members={users}
				onRemove={removeUser}
				onRoleChange={updateUserRole}
				currentUserEmail={userEmail}
				assignableRoles={assignableRoles}
				canDelete={canDeleteUsers}
				canEditRole={canWriteUsers}
			/>
			{canWriteUsers && (
				<InviteUser
					onUserAdd={addUser}
					organization={userOrganizationReadable}
					assignableRoles={assignableRoles}
				/>
			)}
		</div>
	);
}

function ListMembers(props: {
	members: VelavuUser[];
	onRemove: (user: VelavuUser) => void;
	onRoleChange: (user: VelavuUser, role: VelavuRole) => void;
	currentUserEmail?: string;
	assignableRoles: VelavuRole[] | null;
	canDelete?: boolean;
	canEditRole?: boolean;
}) {
	const { t } = useTranslation();
	return (
		<div>
			<p className={styles.title}>
				{t("settings.organization.members_label")}
			</p>
			<VelavuScrollableRoundTable
				className={styles.table}
				layout={[
					{ flex: 1 },
					{ flex: 1 },
					{ flex: 0.6 },
					{ flex: 0.4 },
					{ width: 30, alignment: "end" },
				]}
			>
				<thead>
					<tr>
						<th>{t("settings.organization.table_name")}</th>
						<th>{t("settings.organization.table_email")}</th>
						<th>
							<span className={styles.roleHeader}>
								{t("settings.organization.table_role")}
								<SettingsRolesTooltip />
							</span>
						</th>
						<th>{t("settings.organization.table_status")}</th>
						<th />
					</tr>
				</thead>

				<tbody>
					{props.members.map((user) => {
						const isCurrentUser =
							user.email === props.currentUserEmail;

						// Allow managing this user if the user is not the current user,
						// and the user's role is within the assignable roles.
						const allowManageUser =
							!isCurrentUser &&
							props.assignableRoles !== null &&
							props.assignableRoles.includes(user.role);

						const canRemove = props.canDelete && allowManageUser;

						return (
							<MemberTableRow
								user={user}
								key={user.email}
								canRemove={canRemove}
								canRemoveTooltip={
									canRemove
										? getPermissionDeniedMessage(
												"users:delete",
												t,
											)
										: undefined
								}
								onRemove={() => props.onRemove(user)}
								canEditRole={
									props.canEditRole && allowManageUser
								}
								assignableRoles={props.assignableRoles}
								onRoleChange={(role) =>
									props.onRoleChange(user, role)
								}
							/>
						);
					})}
				</tbody>
			</VelavuScrollableRoundTable>
		</div>
	);
}

function MemberTableRow(props: {
	user: VelavuUser;
	canRemove?: boolean;
	canRemoveTooltip?: string;
	onRemove: VoidFunction;
	canEditRole?: boolean;
	assignableRoles: VelavuRole[] | null;
	onRoleChange: (role: VelavuRole) => void;
}) {
	const { t } = useTranslation();

	const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
	const openMenu = useCallback((event: React.MouseEvent<HTMLElement>) => {
		event.stopPropagation();
		setMenuAnchor(event.currentTarget);
	}, []);
	const closeMenu = useCallback(() => {
		setMenuAnchor(null);
	}, []);

	const { onRemove: propsOnRemove } = props;
	const handleRemove = useCallback(() => {
		closeMenu();
		propsOnRemove();
	}, [closeMenu, propsOnRemove]);

	const isActive = props.user.status === VelavuUserStatus.Confirmed;

	return (
		<>
			<VelavuMenu
				open={!!menuAnchor}
				onClose={closeMenu}
				refPosition={menuAnchor}
				align="right"
			>
				<VelavuMenuItem
					onClick={handleRemove}
					label={t("settings.organization.remove")}
					danger
				/>
			</VelavuMenu>

			<tr className={styles.tableRow}>
				<td className={styles.overflow}>
					<HoverTooltip
						fixed
						tooltip={props.user.name}
						wrapperClassName={styles.overflowContent}
					>
						{props.user.name}
					</HoverTooltip>
				</td>
				<td className={styles.overflow}>
					<HoverTooltip
						fixed
						tooltip={props.user.email}
						wrapperClassName={styles.overflowContent}
					>
						{props.user.email}
					</HoverTooltip>
				</td>
				<td>
					{props.canEditRole ? (
						<div className={styles.roleCell}>
							<span className={styles.roleText}>
								{mapUserRole(props.user.role, t)}
							</span>

							<VelavuInlineSelect
								className={styles.roleSelect}
								value={props.user.role}
								onChange={props.onRoleChange}
							>
								{ROLE_HIERARCHY.map((role) => {
									const isDisabled =
										props.assignableRoles === null ||
										!props.assignableRoles.includes(role);

									return (
										<option
											key={role}
											value={role}
											disabled={isDisabled}
										>
											{mapUserRole(role, t)}
										</option>
									);
								})}
							</VelavuInlineSelect>
						</div>
					) : (
						mapUserRole(props.user.role, t)
					)}
				</td>
				<td>
					<span
						className={classArr(
							isActive
								? styles.statusActive
								: styles.statusInvited,
						)}
					>
						{mapUserRegistrationStatus(props.user.status, t)}
					</span>
				</td>
				<td>
					<VelavuOverflowButton
						onClick={openMenu}
						open={!!menuAnchor}
						disabled={!props.canRemove}
						tooltip={props.canRemoveTooltip}
					/>
				</td>
			</tr>
		</>
	);
}

function InviteUser(props: {
	onUserAdd: (user: VelavuUser) => void;
	organization: string;
	assignableRoles: VelavuRole[] | null;
}) {
	const { t } = useTranslation();
	const [nameInput, setNameInput] = useState("");
	const [emailInput, setEmailInput] = useState("");
	const [roleInput, setRoleInput] = useState(VelavuRole.Viewer);
	const [isLoading, setLoading] = useState(false);

	const [result, setResult] = useState<
		{ success: boolean; description: string } | undefined
	>(undefined);

	const onUserAdd = props.onUserAdd;
	const submit = useCallback(
		(event: FormEvent<HTMLFormElement>) => {
			event.preventDefault();

			setLoading(true);

			VelavuAPI.users
				.registerNewUser({
					name: nameInput,
					email: emailInput,
					role: roleInput,
				})
				.then((result) => {
					setResult({
						success: true,
						description: t("settings.organization.invited", {
							email: emailInput,
							organization: props.organization,
						}),
					});
					setNameInput("");
					setEmailInput("");
					setRoleInput(VelavuRole.Viewer);

					//Calling the callback
					onUserAdd(result);
				})
				.catch((error) => {
					console.log(error.response);

					if (error.response.status === 409) {
						setResult({
							success: false,
							description: t(
								"settings.organization.already_member",
								{
									email: emailInput,
									organization: props.organization,
								},
							),
						});
					} else {
						setResult({
							success: false,
							description: t(
								"settings.organization.invite_failed",
								{
									email: emailInput,
									organization: props.organization,
								},
							),
						});
					}
				})
				.finally(() => {
					setLoading(false);
				});
		},
		[
			props.organization,
			nameInput,
			emailInput,
			roleInput,
			setLoading,
			onUserAdd,
			t,
		],
	);

	return (
		<form className={styles.sectionInvite} onSubmit={submit}>
			<span className={`${styles.title}`}>
				{t("settings.organization.invite_title")}
			</span>

			<div className={styles.sectionInputs}>
				<VelavuInput
					placeholder={t("settings.organization.name_placeholder")}
					value={nameInput}
					onChange={setNameInput}
					disabled={isLoading}
					autoComplete="name"
					fullWidth
					bottomPadding
				/>

				<VelavuInput
					placeholder={t("settings.organization.email_placeholder")}
					value={emailInput}
					onChange={setEmailInput}
					disabled={isLoading}
					autoComplete="email"
					fullWidth
					bottomPadding
				/>

				<VelavuSelect
					value={roleInput}
					onChange={setRoleInput}
					disabled={isLoading}
					fullWidth
					bottomPadding
				>
					{ROLE_HIERARCHY.map((role) => {
						const isDisabled =
							props.assignableRoles === null ||
							!props.assignableRoles.includes(role);

						return (
							<option
								key={role}
								value={role}
								disabled={isDisabled}
							>
								{mapUserRole(role, t)}
							</option>
						);
					})}
				</VelavuSelect>
			</div>

			<VelavuButton
				label={t("settings.organization.send_invite")}
				disabled={
					isLoading ||
					nameInput.length === 0 ||
					!checkEmail(emailInput)
				}
				style={{ alignSelf: "flex-end" }}
				submit
			/>

			{result && (
				<VelavuNotice
					className={styles.statusNotice}
					type={result.success ? "confirm" : "error"}
				>
					<VelavuNoticeHeader>
						{result.success
							? t("settings.organization.user_invited")
							: t("settings.organization.user_invite_failed")}
					</VelavuNoticeHeader>

					<VelavuNoticeBody>{result.description}</VelavuNoticeBody>
				</VelavuNotice>
			)}
		</form>
	);
}
